import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { presenceApi, type PresenceViewer } from "../api/endpoints";
import { useSession } from "./useSession";

const HEARTBEAT_MS = 15_000;

// Fetches the initial snapshot for a launch and keeps ["presence","launch",id]
// populated. SSE delta events (presence.card) are merged by useLaunchEvents.
// Mount this once per Board so every card / modal can read from the cache.
export function useLaunchPresence(launchId: string | null | undefined): void {
  useQuery({
    queryKey: ["presence", "launch", launchId],
    queryFn: async () => (await presenceApi.snapshotLaunch(launchId!)).viewers,
    enabled: !!launchId,
    staleTime: Infinity,
  });
}

// Announces "I'm viewing this card" via heartbeat while the component is
// mounted, and calls leave on unmount / page hide. Safe to mount without a
// cardId — it no-ops until cardId is present.
export function useAnnounceCardPresence(cardId: string | null | undefined): void {
  useEffect(() => {
    if (!cardId) return;
    let cancelled = false;

    const beat = async () => {
      try {
        await presenceApi.heartbeatCard(cardId);
      } catch {
        // server restart, network blip — next interval will retry.
      }
    };
    void beat();
    const timer = setInterval(() => {
      if (!cancelled) void beat();
    }, HEARTBEAT_MS);

    const leave = () => {
      // Fire-and-forget. sendBeacon would survive unload but requires a POST
      // body; our endpoint is bodyless so we fall back to fetch keepalive.
      try {
        fetch(`/api/v1/presence/card/${cardId}/leave`, {
          method: "POST",
          headers: buildAuthHeaders(),
          keepalive: true,
        }).catch(() => undefined);
      } catch {
        // best-effort
      }
    };
    const onPageHide = () => leave();
    window.addEventListener("pagehide", onPageHide);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("pagehide", onPageHide);
      leave();
    };
  }, [cardId]);
}

function buildAuthHeaders(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem("lplanner.token");
    return raw ? { Authorization: `Bearer ${raw}` } : {};
  } catch {
    return {};
  }
}

export function useCardViewers(
  launchId: string | null | undefined,
  cardId: string | null | undefined,
  excludeSelf = true,
): PresenceViewer[] {
  const session = useSession();
  // Subscribe to the same cache entry useLaunchPresence populates so SSE
  // deltas from useLaunchEvents trigger a re-render here.
  const { data: map } = useQuery<Record<string, PresenceViewer[]>>({
    queryKey: ["presence", "launch", launchId],
    enabled: false,
    initialData: () => ({}),
  });
  return useMemo(() => {
    if (!cardId || !map) return [];
    const viewers = map[cardId] ?? [];
    if (!excludeSelf) return viewers;
    const self = session.data?.id;
    return self ? viewers.filter((v) => v.id !== self) : viewers;
  }, [map, cardId, excludeSelf, session.data?.id]);
}
